import React from 'react';
import { cn } from '@/lib/utils';
import { MethodNavGroup, type NavMethod } from '../MethodNavGroup';
import { ControlRow } from '../ControlRow';
import { NumberInput } from '@/components/ui/number-input';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface FixedPremiumShift {
  label: string;
  workHours: number;
  restHours: number | null;
  surchargeTriggered: boolean;
  basePay: number;
  totalPay: number;
}

export interface FixedPremiumBreakdownProps {
  shifts: FixedPremiumShift[];
  surchargeAmount: number;
  thresholdHours: number;
  consistencyCallout?: React.ReactNode;
  /** Deprecated: retained for API compatibility with existing injectors. */
  roleContext?: string;
  /** Deprecated: retained for API compatibility with existing injectors. */
  onRoleCycle?: () => void;
  /** Deprecated: retained for API compatibility with existing injectors. */
  roleKey?: string | number;
  navMethods?: NavMethod[];
  activeMethodName?: string;
  onNavMethodChange?: (methodValue: string) => void;
  onSurchargeAmountChange?: (value: number | null) => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export const FixedPremiumBreakdown: React.FC<FixedPremiumBreakdownProps> = ({
  shifts,
  surchargeAmount,
  thresholdHours,
  consistencyCallout,
  navMethods,
  activeMethodName,
  onNavMethodChange,
  onSurchargeAmountChange,
}) => {
  void shifts;
  void thresholdHours;

  return (
    <div className="space-y-0">
      {/* Method nav group */}
      {navMethods && navMethods.length > 1 && activeMethodName && (
        <div className="flex flex-col items-center mb-2 pb-2 border-b border-border/20">
          <MethodNavGroup
            methods={navMethods}
            activeMethodName={activeMethodName}
            onMethodChange={onNavMethodChange}
          />
        </div>
      )}
      <div className="w-full mb-2">
        <div className="w-full h-6 flex items-center justify-start px-0 text-left font-mono text-[15px] font-semibold tabular-nums">
          <div
            data-testid="fixed-premium-formula-row"
            className="inline-flex items-center gap-3"
          >
            <span className="text-muted-foreground/75">Shift Surcharge:</span>
            <span className="inline-flex items-center rounded-[0.25rem] border border-primary/20 bg-primary/10 px-2 py-1 leading-none text-primary-text">
              +${fmt(surchargeAmount)}
            </span>
          </div>
        </div>
      </div>

      {consistencyCallout && (
        <div>
          {consistencyCallout}
        </div>
      )}

      {onSurchargeAmountChange && (
        <div className={cn(
          'rounded-[0.25rem] border border-border/30 bg-surface-2/30 divide-y divide-border/20',
          consistencyCallout ? 'mt-3' : 'mt-2',
        )}>
          <ControlRow label="Fixed Surcharge" description="Flat dollar amount added to the shift total when a short turn is triggered.">
            <NumberInput
              aria-label="Fixed surcharge amount"
              unit="$"
              unitPosition="left"
              size="xs"
              touchTarget
              value={Number.isFinite(surchargeAmount) ? surchargeAmount : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') { onSurchargeAmountChange(null); return; }
                const parsed = parseFloat(v);
                onSurchargeAmountChange(Number.isFinite(parsed) ? Math.max(0, parsed) : null);
              }}
              minValue={0}
              step={5}
            />
          </ControlRow>
        </div>
      )}
    </div>
  );
};
